// bootLang — the boot-language seam: the one place the shell hands the
// PRE-BUNDLE loader (index.html) a language it can read synchronously, on the
// next load, with no network request before first paint.
// (WS-BOOT-LOADER-PAINTS-ENGLISH-ONLY-BEFORE-THE-BUNDLE-1.)
//
// WHY A CACHE AT ALL. The overlay in index.html is the FIRST paint. It cannot
// read root state and it cannot await a lookup without painting the wrong
// language first and correcting it after — the exact flash the theme pre-seed
// beside it exists to prevent. So the only correct answer is a synchronous
// read of what we learned LAST time. This is the same shape, and the same
// reasoning, as `cacheWhitelabelBootIdentity` in whitelabelBrand.js.
//
// ⚠️ WHAT THIS MAY NOT BECOME (UI-LANG-WORKSPACE-DECIDES-ENGLISH-DEFAULT-1;
// THE LAW lives in shared/functions/languages.js `resolveLang`). A workspace
// renders in the language IT is set to; a workspace set to nothing renders
// English. A person's account preference and this device's `localStorage.lang`
// never decide a workspace's interface language. So:
//   • The entry is keyed by the ARRIVAL'S WORKSPACE SCOPE, never by the
//     account and never once per browser. A scope with no entry reads English
//     — a MISS is the law's default, never another workspace's language.
//   • Nothing here is a CHOICE. `setLang`'s `transient` contract already
//     refuses to persist a RESOLVED language to `localStorage.lang` or to the
//     account, precisely so a workspace's rule cannot follow a member out of
//     the workspace that made it. This cache cannot do that either: it is
//     per host AND per workspace scope, so it is unreadable from anywhere the
//     law would not have answered the same way.
//
// HOST-KEYED, like `wlBoot:` — one browser can visit several white-label
// tenants, and a shared key would paint one tenant's language on another's
// domain.
//
// BOUNDED — at most 8 scopes per host, oldest dropped first (insertion order
// on a plain object). A language code is two bytes; the cap is there so a
// browser that visits many workspaces cannot grow this without limit.
//
// Best-effort by construction: a private-mode or quota-full localStorage must
// never break a boot, so every failure is swallowed and the next boot falls
// back to the next level of the precedence (the tenant brand, else English).
//
// ── FA204 / frank trap ────────────────────────────────────────────────────
// `functions/` is a frank SECTION BARREL: every export becomes its own
// emitted entry, and a bare module-scope const read from inside an exported
// function is unresolvable in the eval-revived runtime. So the key prefix,
// the shipped-language list and the entry cap are INLINED in every export
// that needs them — the same shape `languages.js`, `setLang.js` and
// `activeWorkspacePointer.js` use for their own lists.

/**
 * The scope key an arrival's boot language is cached under — the workspace
 * identity the loader can know SYNCHRONOUSLY, before any bundle.
 *
 * Mirrors `resolveActiveWorkspace`'s own precedence (shared/functions/
 * activeWorkspacePointer.js), because that is the function that decides the
 * REAL scope once the live boot resolves. Any drift means a warm boot either
 * misses its own entry (safe, just no win) or — far worse — reads an entry
 * the live boot would not have used.
 *
 *   • MWT on and the URL carries `/w/<org>[/<seg2>]` → the URL wins, exactly
 *     as the live resolver's URL branch does: `u:<org>` or `u:<org>/<seg2>`.
 *     The URL is NOT blended with the pointer, so an arrival at a workspace
 *     that has no entry yet CANNOT fall through to the previous workspace's.
 *   • otherwise (MWT off, or no `/w/` prefix) → the tab's own
 *     active-workspace pointer, which is what the live resolver uses then:
 *     `p:<id>`. A switch moves the pointer, so it is a different key.
 *   • neither → `null`: no scope is knowable, so there is no cache level and
 *     the precedence falls to the tenant brand, else English.
 *
 * Pure. The caller supplies the inputs; nothing here reads the document.
 *
 * @param {object} sources
 * @param {string} [sources.pathname]   `location.pathname`
 * @param {string} [sources.pointer]    the tab's active-workspace pointer id
 * @param {boolean} [sources.mwtEnabled] default true (the shipped default)
 * @returns {string|null}
 */
export const bootLangScopeKey = ({ pathname, pointer, mwtEnabled = true } = {}) => {
  // The SAME two expressions `getWorkspaceRawSegments` uses — inlined, never
  // read off that sibling, so the emitted entry resolves (FA204/FA001).
  const ONE = /^\/w\/([a-z0-9-]{1,48})(?=[/?#]|$)/
  const TWO = /^\/w\/([a-z0-9-]{1,48})\/([A-Za-z0-9_-]{1,64})(?=[/?#]|$)/
  const path = typeof pathname === 'string' ? pathname : ''
  if (mwtEnabled && path) {
    const two = path.match(TWO)
    if (two) return 'u:' + two[1].toLowerCase() + '/' + two[2].toLowerCase()
    const one = path.match(ONE)
    if (one) return 'u:' + one[1].toLowerCase()
  }
  const id = typeof pointer === 'string' ? pointer.trim() : ''
  return id ? 'p:' + id : null
}

/**
 * The language cached for one host + scope, or `null`.
 *
 * An unshipped or malformed value is `null`, not honoured — a stale entry
 * naming a language whose bundles were never built must never strand a boot,
 * the same refusal `resolveLang` applies at every level.
 *
 * @param {string} hostname
 * @param {string|null} scope  from `bootLangScopeKey`
 * @param {object} [opts]
 * @param {object} [opts.store] a Storage-like bag; defaults to localStorage
 * @returns {string|null}
 */
export const readBootLang = (hostname, scope, { store } = {}) => {
  const SUPPORTED = ['en', 'ka']
  try {
    const bag =
      store || (typeof globalThis !== 'undefined' ? globalThis.localStorage : null) || null
    if (!bag || !hostname || !scope) return null
    const raw =
      typeof bag.getItem === 'function'
        ? bag.getItem('wsBootLang:' + hostname)
        : bag['wsBootLang:' + hostname]
    const map = JSON.parse(raw || 'null')
    if (!map || typeof map !== 'object' || Array.isArray(map)) return null
    const code = map[scope]
    return typeof code === 'string' && SUPPORTED.indexOf(code) !== -1 ? code : null
  } catch (_) {
    return null
  }
}

/**
 * Record the language this arrival's workspace RESOLVED to, for the next
 * load's first paint. Returns true when something was written.
 *
 * Only a shipped code is ever written, so a corrupt entry cannot be minted
 * here. The newest scope is written last, which is what makes the cap drop
 * the oldest.
 *
 * @param {string} hostname
 * @param {string|null} scope  from `bootLangScopeKey`
 * @param {string} lang
 * @param {object} [opts]
 * @param {object} [opts.store] a Storage-like bag; defaults to localStorage
 * @returns {boolean}
 */
export const writeBootLang = (hostname, scope, lang, { store } = {}) => {
  const SUPPORTED = ['en', 'ka']
  const MAX_SCOPES = 8
  try {
    const bag =
      store || (typeof globalThis !== 'undefined' ? globalThis.localStorage : null) || null
    if (!bag || !hostname || !scope) return false
    if (typeof lang !== 'string' || SUPPORTED.indexOf(lang) === -1) return false
    const key = 'wsBootLang:' + hostname
    const get = (k) => (typeof bag.getItem === 'function' ? bag.getItem(k) : bag[k])
    const set = (k, v) => {
      if (typeof bag.setItem === 'function') bag.setItem(k, v)
      else bag[k] = v
    }
    let map = null
    try {
      const parsed = JSON.parse(get(key) || 'null')
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) map = parsed
    } catch (_) {
      map = null
    }
    const next = {}
    for (const k in map || {}) {
      if (k === scope) continue
      const code = map[k]
      if (typeof code === 'string' && SUPPORTED.indexOf(code) !== -1) next[k] = code
    }
    // Re-inserting the scope last keeps insertion order == recency, so the
    // drop below removes the LEAST recently resolved scope.
    next[scope] = lang
    const keys = Object.keys(next)
    const trimmed = {}
    for (const k of keys.slice(Math.max(0, keys.length - MAX_SCOPES))) trimmed[k] = next[k]
    set(key, JSON.stringify(trimmed))
    return true
  } catch (_) {
    return false
  }
}

/**
 * Stamp the ARRIVAL LANGUAGE HINT on a URL whose destination is a COLD load
 * of the shell (WS-ARRIVAL-URL-CARRIES-NO-LANGUAGE-HINT-1).
 *
 * The producer half of the loader's level 1. The two warm levels above are
 * keyed by HOST, so a cross-HOST hand-off is a cold first paint however long
 * the sender has been running: `wsBootLang:<newhost>` is empty and
 * `wlBoot:<newhost>` is empty. The sender, though, resolved the destination
 * workspace's language seconds ago — this is the one way it can say so
 * before the destination's first frame.
 *
 * THE CALLER'S HALF OF THE CONTRACT: pass a language the sender KNOWS is the
 * destination workspace's, never one it guessed. A wrong hint self-heals
 * inside one boot, but a guessed one paints a workspace in a language nobody
 * chose for it — worse than the English default the law already prescribes.
 *
 * THIS FUNCTION'S HALF:
 *   • only a shipped code is ever stamped — anything else returns the URL
 *     unchanged, so a caller never has to pre-validate;
 *   • a `lang` the URL ALREADY carries is never overwritten — the nearest
 *     sender to the arrival wins, and a re-stamp could contradict it;
 *   • the hint lands in the QUERY, which is where the loader reads it, and
 *     the FRAGMENT comes through byte for byte. Pure string surgery, never
 *     `new URL`, because a hand-off fragment carries bearer tokens that must
 *     not be re-encoded.
 *
 * Nothing here persists anything: the hint binds to the arrival alone.
 *
 * @param {string} url  an absolute or root-relative URL
 * @param {string} lang the destination workspace's resolved language
 * @returns {string} the URL, with the hint when one was owed
 */
export const appendBootLangHint = (url, lang) => {
  const SUPPORTED = ['en', 'ka']
  if (typeof url !== 'string' || !url) return url
  if (typeof lang !== 'string' || SUPPORTED.indexOf(lang) === -1) return url
  const cut = url.indexOf('#')
  const head = cut === -1 ? url : url.slice(0, cut)
  const frag = cut === -1 ? '' : url.slice(cut)
  const q = head.indexOf('?')
  // `(^|&)lang=` and not a bare `lang=`: `?slang=en` is a different parameter.
  if (q !== -1 && /(^|&)lang=/.test(head.slice(q + 1))) return url
  return head + (q === -1 ? '?' : '&') + 'lang=' + lang + frag
}

/**
 * The scope key of THIS arrival, read from the live window the way the
 * pre-bundle loader (index.html) reads it: the MWT flag, the tab's own
 * pointer under MWT (else the shared one), then `bootLangScopeKey`. The write
 * half (`writeBootLangHint`) and the read half (`readBootLangHint`) both key
 * by it, so the warm mount reads exactly the entry the loader painted from.
 *
 * @param {Window} [win]
 * @returns {string|null}
 */
export const bootLangArrivalScope = (win = typeof window !== 'undefined' ? window : null) => {
  try {
    if (!win || !win.location) return null
    // The MWT read `mwtFlag.js` performs, inlined: `?mwt` wins over
    // `localStorage.mwt`, and the shipped default is ON.
    const q = new win.URLSearchParams(win.location.search || '').get('mwt')
    const stored = (() => {
      try {
        return win.localStorage ? win.localStorage.getItem('mwt') : null
      } catch (_) {
        return null
      }
    })()
    const mwtEnabled =
      q === '1' || q === 'on' ? true : q === '0' || q === 'off' ? false : stored !== 'off'
    const pointer = (() => {
      try {
        if (mwtEnabled && win.sessionStorage) {
          const own = win.sessionStorage.getItem('activeWorkspace')
          if (own) return own
        }
        return win.localStorage ? win.localStorage.getItem('activeWorkspace') : null
      } catch (_) {
        return null
      }
    })()
    return bootLangScopeKey({
      pathname: win.location.pathname || '',
      pointer: pointer || '',
      mwtEnabled
    })
  } catch (_) {
    return null
  }
}

/**
 * The live-browser call site: record `lang` for the window's own host and the
 * scope this arrival resolved under. Called from the shell whenever the
 * resolved session language changes (app.js `_publishBootLang`).
 *
 * @param {string} lang
 * @param {Window} [win]
 * @returns {boolean}
 */
export const writeBootLangHint = (lang, win = typeof window !== 'undefined' ? window : null) => {
  const SUPPORTED = ['en', 'ka']
  try {
    if (!win || !win.location || typeof lang !== 'string') return false
    if (SUPPORTED.indexOf(lang) === -1) return false
    return writeBootLang(win.location.hostname, bootLangArrivalScope(win), lang, {
      store: win.localStorage
    })
  } catch (_) {
    return false
  }
}

/**
 * The read half: the language the loader painted from for THIS arrival —
 * the `wsBootLang:<host>` entry under `bootLangArrivalScope` — or `null`.
 * Never another scope's entry: a scope with no entry reads `null`, and the
 * caller falls to the next level (the tenant brand, else English).
 *
 * @param {Window} [win]
 * @returns {string|null}
 */
export const readBootLangHint = (win = typeof window !== 'undefined' ? window : null) => {
  try {
    if (!win || !win.location) return null
    return readBootLang(win.location.hostname, bootLangArrivalScope(win), {
      store: win.localStorage
    })
  } catch (_) {
    return null
  }
}

/**
 * The warm boot row, handed to bootShell with the loader's language on it.
 *
 * Shared bootPrepare paints the warm mount from the row's own language pair
 * (`workspaceLanguage`, written by bootSnapshot.js from the full record). A row
 * written before the pair existed, or from a record with no `settings`,
 * carries none: the warm mount painted English under the loader's Georgian,
 * and step 7b turned it Georgian later (aa33, dev, 2026-10-30, a `ka` session
 * on /user/<slug>: `<html lang>` en at 856 ms, the tab "Profile" at 1494 ms,
 * "პროფილი" at 2622 ms). The row now carries `loaderLang`, the entry the
 * loader read for THIS arrival's scope (`readBootLangHint`). bootPrepare reads
 * it ONLY when the row has no pair: a pair is the record's own word, even an
 * older one. bootShell passes the row through as it is, so the value reaches
 * bootPrepare with no change to bootShell.
 *
 * No row, or no entry for this scope: the row as it is. Never mutates the row.
 *
 * @param {object|null} row  `resolveWarmBootSnapshot()`'s validated row
 * @param {Window} [win]
 * @returns {object|null}
 */
export const withBootLangHint = (row, win = typeof window !== 'undefined' ? window : null) => {
  if (!row || typeof row !== 'object') return row
  const lang = readBootLangHint(win)
  return lang ? { ...row, loaderLang: lang } : row
}
